import { useMemo } from "react";

import styled from "styled-components";

import { useTranslate } from "@similarweb/i18n-provider";
import { filterFactory } from "@similarweb/pro-logic-components";
import type { SelectOption } from "@similarweb/pro-logic-components/dist/filter-factory/src/compound";
import { Flex } from "@similarweb/ui-components/foundations";

import type { IFlattenedCategory } from "app/common/services/categoryService.types";
import { useDebounce } from "app/custom-hooks/useDebounce";
import { creativeTypeToInfoMap } from "app/pages/digital-marketing/ad-intelligence/common/ads/hooks/useAdsGallery";
import { useIndustryTopCampaigns } from "app/pages/digital-marketing/ad-intelligence/common/overview/industry/top-campaigns/useIndustryTopCampaigns";
import { allTrackers } from "app/services/track/track";

import {
    createFilterEmptyState,
    renderFaviconItem,
    toEntityOptions,
} from "../../../shared/filters/entity-filter-helpers";
import { createMultiSelectLabel, createSelectLabel } from "../../../shared/filters/label-helpers";

import type { IIndustryAdsFilterState } from "./industryAds.types";
import { INDUSTRY_CREATIVE_TYPES } from "./IndustryAdsFormatFilter";
import type { IndustryCreativeType } from "./IndustryAdsFormatFilter";
import { useIndustryAdsEntityFilter } from "./useIndustryAdsFilterValues";

const FORMAT_TRACKING_ALL = "all";

const NUMBER_OF_RESULTS_TO_SHOW = 100;
const DATA_FETCH_PAGE = 1;
const DEBOUNCE_DELAY = 600;

const Filters = filterFactory.createTypedFilters<IIndustryAdsFilterState>();

// Campaign labels can be very long landing-page URLs — clip them to a single
// ellipsized line and expose the full value via the native title tooltip.
const TruncatedLabel = styled.div`
    max-width: 460px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const renderTruncatedItem = (item: SelectOption) => (
    <TruncatedLabel title={typeof item.label === "string" ? item.label : undefined}>{item.label}</TruncatedLabel>
);

interface IIndustryAdsFilters {
    category: IFlattenedCategory;
    selectedCampaigns: string[];
    onCampaignsFilterChange: (campaigns: string[]) => void;
    selectedAdvertisers: string[];
    onAdvertisersFilterChange: (advertisers: string[]) => void;
    selectedPublishers: string[];
    onPublishersFilterChange: (publishers: string[]) => void;
    selectedFormat?: IndustryCreativeType;
    onFormatFilterChange: (format?: IndustryCreativeType) => void;
}

export const IndustryAdsFilters = ({
    category,
    selectedCampaigns,
    onCampaignsFilterChange,
    selectedAdvertisers,
    onAdvertisersFilterChange,
    selectedPublishers,
    onPublishersFilterChange,
    selectedFormat,
    onFormatFilterChange,
}: IIndustryAdsFilters) => {
    const { translate } = useTranslate();
    const [debouncedCampaignSearchTerm, setCampaignSearchTerm, campaignSearchTerm] = useDebounce("", DEBOUNCE_DELAY);

    const { rows: campaignRows, isLoading: isCampaignsLoading } = useIndustryTopCampaigns(
        category,
        DATA_FETCH_PAGE,
        NUMBER_OF_RESULTS_TO_SHOW,
        debouncedCampaignSearchTerm,
    );

    // Covers the debounce window too, so the empty state never flashes "no results" mid-typing.
    const isCampaignSearchPending = isCampaignsLoading || campaignSearchTerm !== debouncedCampaignSearchTerm;

    const advertiserFilter = useIndustryAdsEntityFilter(category, "advertisers");
    const publisherFilter = useIndustryAdsEntityFilter(category, "publishers");

    const getEmptyState = createFilterEmptyState(
        translate("ad.intelligence.verification.filters.loading"),
        translate("dropdown.no.results"),
    );

    const isCampaignsInitialLoading = isCampaignSearchPending && !campaignSearchTerm && !campaignRows.length;

    const campaignOptions: SelectOption[] = useMemo(() => {
        const seen = new Map<string, SelectOption>();
        campaignRows.forEach((row) => {
            if (row.campaignId && !seen.has(row.campaignId)) {
                seen.set(row.campaignId, { id: row.campaignId, value: row.campaignId, label: row.campaignName });
            }
        });
        return Array.from(seen.values());
    }, [campaignRows]);

    const advertiserOptions: SelectOption[] = useMemo(
        () => toEntityOptions(advertiserFilter.values, selectedAdvertisers, Boolean(advertiserFilter.searchTerm)),
        [advertiserFilter.values, selectedAdvertisers, advertiserFilter.searchTerm],
    );

    const publisherOptions: SelectOption[] = useMemo(
        () => toEntityOptions(publisherFilter.values, selectedPublishers, Boolean(publisherFilter.searchTerm)),
        [publisherFilter.values, selectedPublishers, publisherFilter.searchTerm],
    );

    const formatOptions: SelectOption[] = useMemo(
        () =>
            INDUSTRY_CREATIVE_TYPES.map((type) => ({
                id: type,
                value: type,
                label: translate(creativeTypeToInfoMap[type].name),
                icon: creativeTypeToInfoMap[type].icon,
            })),
        [translate],
    );

    const state: IIndustryAdsFilterState = useMemo(
        () => ({
            campaigns: selectedCampaigns,
            advertisers: selectedAdvertisers,
            publishers: selectedPublishers,
            format: selectedFormat,
        }),
        [selectedCampaigns, selectedAdvertisers, selectedPublishers, selectedFormat],
    );

    const handleFilterChange = (newState: IIndustryAdsFilterState) => {
        if (newState.campaigns !== state.campaigns) {
            onCampaignsFilterChange(newState.campaigns ?? []);
            setCampaignSearchTerm("");
        }
        if (newState.advertisers !== state.advertisers) {
            onAdvertisersFilterChange(newState.advertisers ?? []);
            advertiserFilter.setSearchTerm("");
        }
        if (newState.publishers !== state.publishers) {
            onPublishersFilterChange(newState.publishers ?? []);
            publisherFilter.setSearchTerm("");
        }
        if (newState.format !== state.format) {
            allTrackers.dispatchTrackEvent(
                "Drop Down",
                "click",
                `Filter Format/${newState.format ?? FORMAT_TRACKING_ALL}`,
            );
            onFormatFilterChange(newState.format);
        }
    };

    return (
        <Flex
            gap="8px"
            alignItems="center"
            data-automation="industry-ads-filters"
        >
            <Filters.Provider
                state={state}
                onChange={handleFilterChange}
            >
                <Filters.MultiSelect
                    name="campaigns"
                    label={createMultiSelectLabel(
                        translate("traffic.sources.display.ads.campaigns.chipdown.text"),
                        (id) => campaignOptions.find((option) => option.id === id),
                        (option) => option.label,
                    )}
                    tooltip={translate("ad.intelligence.ads.widget.filters.campaigns")}
                    withSearch
                    searchValue={campaignSearchTerm}
                    onSearch={setCampaignSearchTerm}
                    isLoading={isCampaignSearchPending}
                    isDisabled={isCampaignsInitialLoading}
                    emptyState={getEmptyState(isCampaignSearchPending, campaignOptions)}
                    options={campaignOptions}
                    renderItem={renderTruncatedItem}
                    placeholder={translate("traffic.sources.display.ads.campaigns.chipdown.placeholder")}
                    width="520px"
                />
                <Filters.MultiSelect
                    name="publishers"
                    label={createMultiSelectLabel(
                        translate("ad.intelligence.overview.top.publishers.column.title"),
                        (id) => publisherOptions.find((option) => option.id === id),
                        (option) => option.label,
                    )}
                    tooltip={translate("ad.intelligence.ads.widget.filters.publisher.tooltip")}
                    withSearch
                    searchValue={publisherFilter.searchTerm}
                    onSearch={publisherFilter.setSearchTerm}
                    isLoading={publisherFilter.isSearchPending}
                    isDisabled={publisherFilter.isInitialLoading}
                    emptyState={getEmptyState(publisherFilter.isSearchPending, publisherOptions)}
                    options={publisherOptions}
                    renderItem={renderFaviconItem}
                    placeholder={translate("ad.intelligence.ads.widget.filters.publisher.placeholder")}
                    width="320px"
                />
                <Filters.MultiSelect
                    name="advertisers"
                    label={createMultiSelectLabel(
                        translate("ad.intelligence.ads.table.advertiser.title"),
                        (id) => advertiserOptions.find((option) => option.id === id),
                        (option) => option.label,
                    )}
                    tooltip={translate("ad.intelligence.ads.widget.filters.advertiser.tooltip")}
                    withSearch
                    searchValue={advertiserFilter.searchTerm}
                    onSearch={advertiserFilter.setSearchTerm}
                    isLoading={advertiserFilter.isSearchPending}
                    isDisabled={advertiserFilter.isInitialLoading}
                    emptyState={getEmptyState(advertiserFilter.isSearchPending, advertiserOptions)}
                    options={advertiserOptions}
                    renderItem={renderFaviconItem}
                    placeholder={translate("ad.intelligence.ads.widget.filters.advertiser.placeholder")}
                    width="320px"
                />
                <Filters.Select
                    name="format"
                    label={createSelectLabel(
                        translate("ad.intelligence.ads.widget.filters.format"),
                        (id) => formatOptions.find((option) => option.id === id),
                        (option) => option.label,
                    )}
                    tooltip={translate("ad.intelligence.ads.widget.filters.format.tooltip")}
                    withSearch={false}
                    options={formatOptions}
                    width="140px"
                />
            </Filters.Provider>
        </Flex>
    );
};
